// Framework and third-party non-ui
import * as React from 'react';
import { useAnimation } from 'framer-motion';

// Hooks, context, and constants
import { useMapContext } from 'contexts';

// App components

// JSON & Styles
import {
  DrawerWrapper,
  DrawerOverlay,
  DrawerContent,
  StyledTitle,
  StyledTitleContainer,
  StyledContentContainer,
  StyledButtonGroup,
  StyledCloseButton,
  StyledContentRow,
  StyledContentRowTitle,
  StyledContentRowContent,
  StyledTextBox,
  StyledContent,
  StyledIconButton,
  StyledNoteBox,
  StyledTextBoxContainer,
  StyledDeleteButton,
} from './Drawer-styled';
import { savePindrop } from 'components/DropPinWidget/utilities';

// Third-party components (buttons, icons, etc.)
const useCoordFormatter = ({ point }) => {
  if (point) {
    const degree = {
      lat: Math.abs(point.latitude),
      long: Math.abs(point.longitude),
    };

    const minute = {
      lat: (degree.lat % 1) * 60,
      long: (degree.long % 1) * 60,
    };

    const second = {
      lat: (minute.lat % 1) * 60,
      long: (minute.long % 1) * 60,
    };

    const direction = {
      lat: point.latitude >= 0 ? 'N' : 'S',
      long: point.longitude >= 0 ? 'E' : 'W',
    };

    const dd = `${degree.lat.toFixed(5)}\xB0 ${
      direction.lat
    } ${degree.long.toFixed(5)}\xB0 ${direction.long}`;

    const ddm = `${Math.trunc(degree.lat)}\xB0 ${minute.lat.toFixed(3)}' ${
      direction.lat
    } ${Math.trunc(degree.long)}\xB0 ${minute.long.toFixed(3)}' ${
      direction.long
    }`;

    const dms = `${Math.trunc(degree.lat)}\xB0 ${Math.trunc(
      minute.lat
    )}' ${second.lat.toFixed(1)}" ${direction.lat} ${Math.trunc(
      degree.long
    )}\xB0 ${Math.trunc(minute.long)}' ${second.long.toFixed(1)}" ${
      direction.long
    }`;

    return { dd, ddm, dms };
  }

  return {
    dd: null,
    ddm: null,
    dms: null,
  };
};

const getPinName = (layer) => {
  const { title } = layer || {};
  const name = title;
  const alias = name?.replace(/ *\([^)]*\) */g, '');
  const saveType = name?.match(/\(([^)]+)\)/)[1];

  return { name, alias, type: saveType };
};

export const FeatureDrawer = ({
  isOpen,
  features,
  onClose,
  mapView,
  user,
  setIsLoading,
}) => {
  // State and Constants
  const height = 400;
  const overlayColor = 'transparent';

  const { LayersConfig } = window.Pin2Flood;

  const prevContent = React.useRef();

  const popupItem = features?.length > 0 ? features[0] : null;
  const title = popupItem?.title;
  const pinName = getPinName(popupItem?.feature.layer);

  const content = {
    addressCount:
      typeof popupItem?.addressCount === 'number'
        ? popupItem.addressCount
        : 'No flood polygon found.',
    pintype: pinName?.alias,
    geometry: popupItem?.feature.geometry,
    feature: popupItem?.feature,
    userId: popupItem?.feature.attributes[LayersConfig.PINS.fields.userid.name],
    userName:
      popupItem?.feature.attributes[LayersConfig.PINS.fields.fullname.name],
    notes: popupItem?.feature.attributes[LayersConfig.PINS.fields.notes.name],
  };

  const MapContext = useMapContext();
  const pinsLayerGroup = MapContext.getLayer(LayersConfig.PINS.title);
  const floodLayer = MapContext.getLayer(LayersConfig.FLOOD_AREAS.title);
  const pinsLayer = pinsLayerGroup ? pinsLayerGroup[0]?.source?.layer : null;

  const [isEditable, setIsEditable] = React.useState(false);

  const [draftNotes, setDraftNotes] = React.useState(
    popupItem?.feature.attributes[LayersConfig.PINS.fields.notes.name]
  );
  const [notes, setNotes] = React.useState(
    popupItem?.feature.attributes[LayersConfig.PINS.fields.notes.name]
  );

  const { dms } = useCoordFormatter({ point: content.geometry });

  const controls = useAnimation();

  const drawerContentStyles = {
    active: {
      y: window.innerHeight - height,
    },
    inactive: {
      y: window.innerHeight,
    },
  };

  // Actions
  const handleDeletePin = async () => {
    setIsLoading(true);
    console.log(content.feature?.attributes);
    const id =
      content.feature?.attributes[LayersConfig.PINS.fields.globalid.name];
    const params = {
      where: `${LayersConfig.FLOOD_AREAS.fields.pindropid.name}='${id}'`,
      returnGeometry: false,
      outFields: [LayersConfig.FLOOD_AREAS.fields.objectid.name],
      f: 'json',
    };

    let query = floodLayer.createQuery();
    query = { ...query, ...params };
    const response = await floodLayer.queryFeatures(query);
    const floodFeatures = response.features.length ? response.features : null;
    console.log(query, floodFeatures);

    try {
      const deletePoint = await pinsLayer.applyEdits({
        deleteFeatures: [content.feature],
      });
      const deletePoly = floodFeatures
        ? await floodLayer.applyEdits({
            deleteFeatures: floodFeatures,
          })
        : null;

      // TODO: Error handling

      deletePoint &&
        console.log(
          `Pin Point deleted with status: ${
            deletePoint.deleteFeatureResults[0].error ? 'Failed' : 'Success'
          }`
        );

      deletePoly &&
        console.log(
          `Flood Polygon deleted with status: ${
            deletePoly.deleteFeatureResults[0].error ? 'Failed' : 'Success'
          }`
        );

      pinsLayerGroup.forEach((l) => l.refresh());
      floodLayer.refresh();
      setIsLoading(false);
      onClose();
    } catch (err) {
      console.error(err);
      setIsLoading(false);
    }
  };

  const handleCloseDrawer = () => {
    onClose();
  };

  const handleStartEditNotes = () => {
    setIsEditable(true);
    const access =
      document.getElementById('pin notes section');
    access.scrollIntoView({ behavior: 'smooth' }, true);
  };

  const handleEditNotes = (event) => {
    const value = event.target.value;
    setDraftNotes(value);
  };

  const handleSaveEdits = async () => {
    const params = {
      attributes: {
        [pinsLayer.objectIdField]:
          popupItem?.feature.attributes[pinsLayer.objectIdField],
        [LayersConfig.PINS.fields.notes.name]: draftNotes,
      },
      fields: LayersConfig.PINS.fields,
      pinDropLayer: pinsLayer,
    };

    const updateResults = await savePindrop(params);

    if (updateResults.error) {
      console.error(updateResults);
      setIsEditable(false);
      setDraftNotes(notes);
    } else {
      setIsEditable(false);
      setNotes(draftNotes);
    }
  };

  const handleCancelEdits = () => {
    setIsEditable(false);
    setDraftNotes(notes);
  };

  // Effects
  React.useEffect(() => {
    // play open and close animation when not in partial state
    controls.start(isOpen ? 'active' : 'inactive');
  }, [isOpen, controls]);

  React.useEffect(() => {
    const updateExtent = async () => {
      if (isOpen && popupItem) {
        if (popupItem.floodExtent && popupItem.id !== prevContent.current) {
          prevContent.current = popupItem.id;
          mapView.padding.bottom = height;
          await mapView.goTo(popupItem.floodExtent);
        }
      }
    };

    updateExtent();
  }, [isOpen, features]);

  React.useEffect(() => {
    setNotes(
      popupItem?.feature.attributes[LayersConfig.PINS.fields.notes.name]
    );
    setDraftNotes(
      popupItem?.feature.attributes[LayersConfig.PINS.fields.notes.name]
    );
  }, [popupItem]);

  return (
    <DrawerWrapper zIndex={2}>
      <DrawerOverlay
        overlayColor={overlayColor}
        status={isOpen ? 'open' : 'closed'}
      />
      <DrawerContent
        id="feature-popup"
        height={height}
        drag="y"
        dragElastic={0.1}
        dragConstraints={{
          top: window.innerHeight - height,
          bottom: window.innerHeight,
        }}
        dragMomentum={false}
        onDragEnd={(_event, info) => {
          const isDraggingDown = info.offset.y > 0;

          if (isDraggingDown) {
            handleCloseDrawer();
          }
        }}
        animate={controls}
        variants={drawerContentStyles}
        transition={{ type: 'spring', damping: 30, stiffness: 180 }}
      >
        <StyledTitleContainer>
          <StyledTitle>{title}</StyledTitle>
          <StyledCloseButton onClick={handleCloseDrawer}>
            <div className="esri-icon-close" />
          </StyledCloseButton>
        </StyledTitleContainer>

        <StyledContentContainer>
          <StyledContent>
            <StyledContentRow>
              <StyledContentRowTitle>
                {popupItem?.floods?.length > 1
                  ? 'Addresses in these Polygons'
                  : 'Addresses in this Polygon'}
              </StyledContentRowTitle>
              <StyledContentRowContent>
                {content.addressCount}
              </StyledContentRowContent>
            </StyledContentRow>

            <StyledContentRow>
              <StyledContentRowTitle id="pin notes section">
                Notes
              </StyledContentRowTitle>
              {isEditable ? (
                <div>
                  <StyledTextBoxContainer>
                    <StyledTextBox
                      type="text"
                      id="pin notes"
                      name="notes"
                      maxLength="256"
                      value={draftNotes ? draftNotes : ''}
                      onChange={handleEditNotes}
                    />
                  </StyledTextBoxContainer>
                  <div style={{ display: 'flex' }}>
                    <StyledIconButton onClick={handleSaveEdits}>
                      <div
                        aria-hidden="true"
                        className="button-icon esri-icon-save"
                        style={{ padding: 0 }}
                      />
                    </StyledIconButton>
                    <StyledIconButton onClick={handleCancelEdits}>
                      <div
                        aria-hidden="true"
                        className="button-icon esri-icon-close"
                        style={{ padding: 0 }}
                      />
                    </StyledIconButton>
                  </div>
                </div>
              ) : (
                <div>
                  <StyledContentRowContent>
                    {notes?.length > 0 && (
                      <StyledNoteBox>{notes}</StyledNoteBox>
                    )}
                  </StyledContentRowContent>

                  {content.userId === user.id && (
                    <div style={{ display: 'flex' }}>
                      <StyledIconButton onClick={handleStartEditNotes}>
                        <div
                          aria-hidden="true"
                          className="button-icon esri-icon-edit"
                          style={{ padding: 0 }}
                        />
                      </StyledIconButton>
                    </div>
                  )}
                </div>
              )}
            </StyledContentRow>

            <StyledContentRow>
              <StyledContentRowTitle>Pin Coordinates</StyledContentRowTitle>
              <StyledContentRowContent>{dms}</StyledContentRowContent>
            </StyledContentRow>

            <StyledContentRow>
              <StyledContentRowTitle>Pin Type</StyledContentRowTitle>
              <StyledContentRowContent>
                {content.pintype}
              </StyledContentRowContent>
            </StyledContentRow>

            <StyledContentRow>
              <StyledContentRowTitle> Submitted By</StyledContentRowTitle>
              <StyledContentRowContent>
                {content.userName} ({content.userId})
              </StyledContentRowContent>
            </StyledContentRow>
          </StyledContent>
        </StyledContentContainer>

        <StyledButtonGroup>
          {content.userId === user.id && (
            <StyledDeleteButton onClick={handleDeletePin}>
              {' '}
              Delete{' '}
            </StyledDeleteButton>
          )}
        </StyledButtonGroup>
      </DrawerContent>
    </DrawerWrapper>
  );
};
